import { z } from "zod";

const runtimeConfigSchema = z
  .object({
    schemaVersion: z.literal(1),
    convexUrl: z.string(),
    logtoAudience: z.string(),
    simpleappId: z.string().trim().min(1),
    simpleappVersion: z.string().trim().min(1),
  })
  .strict()
  .superRefine((config, context) => {
    for (const key of ["convexUrl", "logtoAudience"] as const) {
      let url: URL;
      try {
        url = new URL(config[key]);
      } catch {
        context.addIssue({
          code: "custom",
          path: [key],
          message: "must be an absolute URL",
        });
        continue;
      }

      if (url.protocol !== "http:" && url.protocol !== "https:") {
        context.addIssue({
          code: "custom",
          path: [key],
          message: "must use http or https",
        });
      }
      if (url.username || url.password) {
        context.addIssue({
          code: "custom",
          path: [key],
          message: "must not contain credentials",
        });
      }
      if (url.hash) {
        context.addIssue({
          code: "custom",
          path: [key],
          message: "must not contain a fragment",
        });
      }
    }
  });

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;

declare global {
  interface Window {
    __SAPPS_RUNTIME_CONFIG__?: unknown;
  }
}

function getRuntimeConfigOverride(): unknown {
  if (typeof window === "undefined") return undefined;
  return window.__SAPPS_RUNTIME_CONFIG__;
}

export function resolveRuntimeConfig(): RuntimeConfig {
  const runtimeConfigOverride = getRuntimeConfigOverride();
  if (runtimeConfigOverride === undefined) {
    throw new Error(
      "[Runtime Config] Missing window.__SAPPS_RUNTIME_CONFIG__. The app cannot start without runtime-config.js.",
    );
  }

  const result = runtimeConfigSchema.safeParse(runtimeConfigOverride);
  if (!result.success) {
    throw new Error(
      `[Runtime Config] Invalid window.__SAPPS_RUNTIME_CONFIG__: ${result.error.message}`,
    );
  }

  return result.data;
}

export const runtimeConfig = resolveRuntimeConfig();
